'use client';

import React, { memo, useMemo } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { 
  ShoppingCart, 
  ChevronRight
} from 'lucide-react';
import { CartItem } from '../types';
import { useRouter } from 'next/navigation';
import { setOrderData, getOrderData } from '../utils/localStorage';

interface OrderCartProps {
  cart: (CartItem & { description?: string | null })[];
  linkCode: string;
}

// Memoized MobileCartPreview component to prevent unnecessary re-renders
const MobileCartPreview = memo(function MobileCartPreview({
  totalItems,
  linkCode,
  cart
}: {
  totalItems: number;
  linkCode: string;
  cart: (CartItem & { description?: string | null })[];
}) {
  const router = useRouter();

  if (totalItems === 0) return null;

  const handleViewCart = () => {
    // Preserve existing cart data and only update items
    const existingCartData = getOrderData('CART_DATA') as any;
    console.log('🔍 OrderCart DEBUG - Existing cart data from localStorage:', existingCartData);
    console.log('🔍 OrderCart DEBUG - Current cart items:', cart);
    console.log('🔍 OrderCart DEBUG - LinkCode:', linkCode);
    
    const cartData = existingCartData ? {
      ...existingCartData,
      items: cart // Update with current cart items
    } : {
      items: cart // Fallback if no existing data
    };
    console.log('🔍 OrderCart DEBUG - Final cart data being saved:', cartData);
    setOrderData('CART_DATA', cartData);
    router.push(`/${linkCode}/cart`);
  };

  return (
    <button 
      onClick={handleViewCart}
      className="bg-green-600 w-full max-w-[850px] mx-auto items-center fixed bottom-0 left-1/2 transform -translate-x-1/2 z-50 shadow-lg cursor-pointer rounded-t-sm"
    >
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-white gap-3">
            <div className="relative">
              <div className='flex text-sm font-semibold'>
              {totalItems} {totalItems === 1 ? 'item' : 'items'} added
              </div>
            </div>
          </div>
          <div className="flex items-center text-white gap-3">
            <span className="text-sm font-medium">View Cart</span>
            <ChevronRight className="h-5 w-5 text-white" />
          </div>
        </div>
      </div>
    </button>
  );
});

// Memoized empty cart component to prevent unnecessary re-renders
const EmptyCart = memo(function EmptyCart() { return (
  <Card className="sticky top-0 z-10 shadow-none border-dashed border-blue-100 rounded-xl hidden">
    <CardContent className="pt-8 pb-8">
      <div className="flex flex-col items-center justify-center text-center space-y-4">
        <div className="relative">
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
            <ShoppingCart className="h-6 w-6 text-primary" />
          </div>
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-muted text-xs">
            0
          </span>
        </div>
        <div className="space-y-2">
          <h3 className="font-semibold tracking-tight">Your cart is empty</h3>
          <p className="text-sm text-muted-foreground">
            Start adding items from the menu to create your order
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
  );
});

// Simplified OrderCart component that only shows mobile cart preview
export const OrderCart = memo(function OrderCart({
  cart,
  linkCode
}: OrderCartProps) {
  // Memoize the calculation to prevent unnecessary recalculation
  const totalItems = useMemo(() => {
    return cart.length;
  }, [cart]);

  // Check for empty cart after all hooks are called
  if (cart.length === 0) {
    return <EmptyCart />;
  }

  return (
    <MobileCartPreview 
      totalItems={totalItems}
      linkCode={linkCode}
      cart={cart}
    />
  );
});